import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as faceapi from 'face-api.js';
import axios from 'axios';

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
    color: '#fff',
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    textAlign: 'center',
    marginBottom: '1rem',
    paddingBottom: '1rem',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
  },
  title: {
    fontSize: '1.5rem',
    color: '#00d9ff',
    marginBottom: '0.25rem',
  },
  subtitle: {
    fontSize: '0.85rem',
    color: 'rgba(255,255,255,0.6)',
  },
  main: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1rem',
  },
  videoContainer: {
    position: 'relative',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
    background: '#000',
    width: '100%',
    maxWidth: '640px',
  },
  video: {
    display: 'block',
    width: '100%',
    height: 'auto',
    transform: 'scaleX(-1)',
  },
  canvas: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    transform: 'scaleX(-1)',
    pointerEvents: 'none',
  },
  status: {
    padding: '0.75rem 1.5rem',
    borderRadius: '8px',
    background: 'rgba(255, 255, 255, 0.1)',
    fontSize: '0.9rem',
    textAlign: 'center',
    maxWidth: '400px',
    width: '100%',
  },
  statusSuccess: {
    background: 'rgba(0, 230, 118, 0.2)',
    color: '#00e676',
    border: '1px solid rgba(0, 230, 118, 0.3)',
  },
  statusError: {
    background: 'rgba(255, 82, 82, 0.2)',
    color: '#ff5252',
    border: '1px solid rgba(255, 82, 82, 0.3)',
  },
  button: {
    padding: '1rem 2rem',
    borderRadius: '8px',
    border: 'none',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  primaryButton: {
    background: 'linear-gradient(135deg, #00d9ff 0%, #0099cc 100%)',
    color: '#000',
  },
  secondaryButton: {
    background: 'rgba(255, 255, 255, 0.1)',
    color: '#fff',
    border: '1px solid rgba(255, 255, 255, 0.2)',
  },
  disabledButton: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  resultCard: {
    width: '100%',
    maxWidth: '400px',
    padding: '1.5rem',
    borderRadius: '12px',
    textAlign: 'center',
  },
  resultMatched: {
    background: 'linear-gradient(135deg, rgba(0, 230, 118, 0.1) 0%, rgba(0, 200, 83, 0.1) 100%)',
    border: '2px solid rgba(0, 230, 118, 0.5)',
  },
  resultUnmatched: {
    background: 'linear-gradient(135deg, rgba(255, 82, 82, 0.1) 0%, rgba(255, 23, 68, 0.1) 100%)',
    border: '2px solid rgba(255, 82, 82, 0.5)',
  },
  avatar: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    margin: '0 auto 1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '2rem',
  },
  avatarMatched: {
    background: 'linear-gradient(135deg, #00e676 0%, #00c853 100%)',
  },
  avatarUnmatched: {
    background: 'linear-gradient(135deg, #ff5252 0%, #ff1744 100%)',
  },
  personName: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    marginBottom: '0.5rem',
  },
  confidence: {
    fontSize: '0.9rem',
    color: 'rgba(255,255,255,0.7)',
  },
  buttonRow: {
    display: 'flex',
    gap: '1rem',
    marginTop: '1rem',
  },
  poweredBy: {
    textAlign: 'center',
    fontSize: '0.75rem',
    color: 'rgba(255,255,255,0.4)',
    marginTop: 'auto',
    paddingTop: '1rem',
  },
  loader: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1rem',
    padding: '3rem',
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '4px solid rgba(255, 255, 255, 0.1)',
    borderTop: '4px solid #00d9ff',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
};

// Add keyframes for spinner
const spinnerKeyframes = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

function WidgetRecognizePage() {
  const [isModelLoaded, setIsModelLoaded] = useState(false);
  const [status, setStatus] = useState('Loading face detection models...');
  const [statusType, setStatusType] = useState('info');
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [stream, setStream] = useState(null);
  const [config, setConfig] = useState({});
  
  const videoRef = useRef();
  const canvasRef = useRef();

  // Parse URL parameters
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setConfig({
      callback: params.get('callback') === 'true',
      source: params.get('source') || 'widget',
      autoClose: params.get('autoClose') === 'true',
      autoCloseDelay: parseInt(params.get('autoCloseDelay')) || 3000,
      parentOrigin: params.get('origin') || '*',
    });
  }, []);

  // Load models
  useEffect(() => {
    const loadModels = async () => {
      try {
        const styleSheet = document.createElement('style');
        styleSheet.textContent = spinnerKeyframes;
        document.head.appendChild(styleSheet);

        await Promise.all([
          faceapi.nets.ssdMobilenetv1.loadFromUri('/models'),
          faceapi.nets.faceLandmark68Net.loadFromUri('/models'),
          faceapi.nets.faceRecognitionNet.loadFromUri('/models'),
        ]);
        
        setIsModelLoaded(true);
        setStatus('Models loaded. Starting camera...');
        startCamera();
      } catch (error) {
        setStatus('Error loading models: ' + error.message);
        setStatusType('error');
      }
    };
    
    loadModels();
    
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const startCamera = async () => {
    try {
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: { width: 640, height: 480, facingMode: 'user' }
      });
      
      setStream(newStream);
      
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = newStream;
          videoRef.current.play().catch(console.error);
        }
        setStatus('Camera ready. Click "Scan Face" to identify.');
        setStatusType('info');
      }, 100);
    } catch (error) {
      setStatus('Camera error: ' + error.message);
      setStatusType('error');
    }
  };

  const sendResultToParent = useCallback((data) => {
    if (config.callback && window.opener) {
      window.opener.postMessage({
        type: data.matched ? 'FACE_RECOGNIZED' : 'FACE_NOT_RECOGNIZED',
        source: config.source,
        ...data,
      }, config.parentOrigin);
      
      if (config.autoClose) {
        setTimeout(() => {
          window.close();
        }, config.autoCloseDelay);
      }
    }
  }, [config]);

  const recognizeFace = useCallback(async () => {
    if (!isModelLoaded || !videoRef.current || isProcessing) return;
    
    setIsProcessing(true);
    setStatus('Detecting face...');
    setStatusType('info');
    setResult(null);
    
    try {
      const detection = await faceapi
        .detectSingleFace(videoRef.current)
        .withFaceLandmarks()
        .withFaceDescriptor();

      if (detection) {
        // Draw detection
        if (canvasRef.current) {
          const displaySize = { 
            width: videoRef.current.videoWidth, 
            height: videoRef.current.videoHeight 
          };
          faceapi.matchDimensions(canvasRef.current, displaySize);
          const resized = faceapi.resizeResults(detection, displaySize);
          const ctx = canvasRef.current.getContext('2d');
          ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
          faceapi.draw.drawDetections(canvasRef.current, resized);
          faceapi.draw.drawFaceLandmarks(canvasRef.current, resized);
        }

        setStatus('Searching database...');
        
        const faceVector = Array.from(detection.descriptor);
        
        const response = await axios.post('/api/faces/recognize', {
          face_vector: faceVector,
          source: config.source,
        });
        
        setResult(response.data);
        
        if (response.data.matched) {
          setStatus(`✅ Identified: ${response.data.person.name}`);
          setStatusType('success');
          
          sendResultToParent({
            matched: true,
            person_id: response.data.person.person_id,
            external_user_id: response.data.person.external_user_id,
            name: response.data.person.name,
            department: response.data.person.department,
            designation: response.data.person.designation,
            confidence: parseFloat(response.data.confidence),
            matched_face: response.data.matched_face,
          });
        } else {
          setStatus('❌ Face not recognized');
          setStatusType('error');
          
          sendResultToParent({
            matched: false,
            message: response.data.message,
          });
        }
      } else {
        setStatus('No face detected. Please position your face in the camera.');
        setStatusType('error');
      }
    } catch (error) {
      setStatus('Error: ' + (error.response?.data?.error || error.message));
      setStatusType('error');
    }
    
    setIsProcessing(false);
  }, [isModelLoaded, isProcessing, config, sendResultToParent]);

  const resetScan = () => {
    setResult(null);
    setStatus('Camera ready. Click "Scan Face" to identify.');
    setStatusType('info');
    
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
  };

  const handleCancel = () => {
    if (config.callback && window.opener) {
      window.opener.postMessage({
        type: 'FACE_RECOGNITION_CANCELLED',
        source: config.source,
      }, config.parentOrigin);
    }
    window.close();
  };

  if (!isModelLoaded) {
    return (
      <div style={styles.container}>
        <div style={styles.loader}>
          <div style={styles.spinner} />
          <p>{status}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>🔐 Face Recognition</h1>
        {config.source && (
          <p style={styles.subtitle}>Source: {config.source}</p>
        )}
      </div>
      
      <div style={styles.main}>
        {/* Video */}
        <div style={styles.videoContainer}>
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            onLoadedMetadata={(e) => e.target.play()}
            style={styles.video}
          />
          <canvas ref={canvasRef} style={styles.canvas} />
        </div>
        
        {/* Status */}
        <div
          style={{
            ...styles.status,
            ...(statusType === 'error' ? styles.statusError : {}),
            ...(statusType === 'success' ? styles.statusSuccess : {}),
          }}
        >
          {status}
        </div>
        
        {/* Result Card */}
        {result && (
          <div
            style={{
              ...styles.resultCard,
              ...(result.matched ? styles.resultMatched : styles.resultUnmatched),
            }}
          >
            <div
              style={{
                ...styles.avatar,
                ...(result.matched ? styles.avatarMatched : styles.avatarUnmatched),
              }}
            >
              {result.matched ? '✓' : '✗'}
            </div>
            {result.matched ? (
              <>
                <div style={styles.personName}>{result.person.name}</div>
                <p style={styles.confidence}>
                  Confidence: {result.confidence}%
                </p>
                {result.person.department && (
                  <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)' }}>
                    {result.person.department} • {result.person.designation}
                  </p>
                )}
                {result.matched_face && (
                  <p style={{ fontSize: '0.8rem', color: '#00d9ff', marginTop: '0.5rem' }}>
                    Matched: {result.matched_face}
                  </p>
                )}
              </>
            ) : (
              <>
                <div style={styles.personName}>Unknown Person</div>
                <p style={styles.confidence}>{result.message}</p>
              </>
            )}
          </div>
        )}
        
        {/* Buttons */}
        <div style={styles.buttonRow}>
          {result ? (
            <>
              <button
                style={{ ...styles.button, ...styles.primaryButton }}
                onClick={resetScan}
              >
                🔄 Scan Again
              </button>
              {config.callback && (
                <button
                  style={{ ...styles.button, ...styles.secondaryButton }}
                  onClick={handleCancel}
                >
                  Close
                </button>
              )}
            </>
          ) : (
            <>
              <button
                style={{
                  ...styles.button,
                  ...styles.primaryButton,
                  ...(isProcessing ? styles.disabledButton : {}),
                }}
                onClick={recognizeFace}
                disabled={isProcessing}
              >
                {isProcessing ? '⏳ Scanning...' : '📸 Scan Face'}
              </button>
              {config.callback && (
                <button
                  style={{ ...styles.button, ...styles.secondaryButton }}
                  onClick={handleCancel}
                >
                  Cancel
                </button>
              )}
            </>
          )}
        </div>
      </div>
      
      <div style={styles.poweredBy}>
        Powered by FaceGate Recognition System
      </div>
    </div>
  );
}

export default WidgetRecognizePage;

